import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Loader2, Trash2, Square } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useSharedAgentChat } from '../../contexts/AgentChatContext';
import ThinkingBlock from './ThinkingBlock';
import ToolCallCard from './ToolCallCard';
import StreamingMarkdown from './StreamingMarkdown';
import type { AgentMessage, AgentMessagePart } from '../../types/agent';

interface AIChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AIChatSidebar: React.FC<AIChatSidebarProps> = ({ isOpen, onClose }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || null;

  const { messages, sendMessage, isStreaming, stopStreaming, clearMessages } =
    useSharedAgentChat();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    sendMessage(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderParts = (msg: AgentMessage) => {
    if (!msg.parts || msg.parts.length === 0) {
      // Still waiting for first event
      if (isStreaming && msg === messages[messages.length - 1]) {
        return (
          <div className="px-3 py-2 rounded-xl bg-[var(--color-bg-tertiary)] rounded-bl-sm">
            <Loader2 className="w-4 h-4 animate-spin text-[var(--color-text-muted)]" />
          </div>
        );
      }
      return null;
    }

    return (
      <div className="max-w-[90%] space-y-0.5">
        {msg.parts.map((part, i) => renderPart(part, i, msg))}
      </div>
    );
  };

  const renderPart = (part: AgentMessagePart, index: number, msg: AgentMessage) => {
    const isLastMsg = msg === messages[messages.length - 1];

    switch (part.type) {
      case 'thinking':
        return (
          <ThinkingBlock
            key={index}
            text={part.text}
            startedAt={part.startedAt}
            completedAt={part.completedAt}
            isStreaming={isStreaming && isLastMsg}
          />
        );

      case 'tool_call':
        return (
          <ToolCallCard
            key={part.eventId}
            toolName={part.toolName}
            args={part.args}
            status={part.status}
            result={part.result}
          />
        );

      case 'text':
        return (
          <StreamingMarkdown
            key={index}
            text={part.text}
            isStreaming={
              isStreaming &&
              isLastMsg &&
              index === (msg.parts?.length || 0) - 1
            }
          />
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
      <div
        className="h-full w-full flex flex-col border-l border-[var(--color-border)]"
        style={{ backgroundColor: 'var(--color-surface)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-[#17c480]" />
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              AI Assistant
            </h2>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[#17c480]/10 text-[#17c480]">
              Preview
            </span>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearMessages}
                className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
                title="Clear chat"
              >
                <Trash2 className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
            >
              <X className="w-4 h-4 text-[var(--color-text-muted)]" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Bot className="w-10 h-10 text-[#17c480]/30 mb-3" />
              <p className="text-sm font-medium text-[var(--color-text-secondary)]">
                AI Assistant
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-1 max-w-[220px]">
                {siteId
                  ? 'Ask questions about your site events, documents, and equipment.'
                  : 'Select a site to get started with AI-powered insights.'}
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-6 h-6 rounded-full bg-[#17c480]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-3.5 h-3.5 text-[#17c480]" />
                </div>
              )}

              {msg.role === 'user' ? (
                <div className="max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed bg-[#17c480] text-white rounded-br-sm">
                  {msg.content}
                </div>
              ) : (
                renderParts(msg)
              )}

              {msg.role === 'user' && (
                <div className="w-6 h-6 rounded-full bg-[var(--color-bg-tertiary)] flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
                </div>
              )}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-[var(--color-border)]">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                siteId
                  ? 'Ask about your site...'
                  : 'Select a site to get started...'
              }
              disabled={!siteId}
              rows={1}
              className="flex-1 px-3 py-2 text-xs rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)]
                         text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]
                         focus:outline-none focus:border-[#17c480]/50 resize-none max-h-24
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                height: 'auto',
                minHeight: '36px',
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 96) + 'px';
              }}
            />
            {isStreaming ? (
              <button
                onClick={stopStreaming}
                className="p-2 rounded-lg bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)] transition-colors"
                title="Stop generating"
              >
                <Square className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim() || !siteId}
                className="p-2 rounded-lg bg-[#17c480] text-white hover:bg-[#15b374] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
  );
};

export default AIChatSidebar;
